import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';

const CartDrawer = ({ isOpen, onClose, cart, updateQuantity, removeFromCart }) => {
  const subtotal = cart.reduce((sum, item) => sum + (item.price * item.qty), 0);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-espresso/60 backdrop-blur-sm z-[70]"
            aria-hidden="true"
          />
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed top-0 right-0 h-full w-full max-w-md bg-cream shadow-2xl z-[80] flex flex-col border-l border-moss/20"
            role="dialog"
            aria-label="Shopping Cart"
          >
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h2 className="font-serif text-2xl font-bold text-espresso">Your Cart</h2>
              <button 
                onClick={onClose}
                aria-label="Close cart"
                className="p-2 text-gray-500 hover:text-espresso transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-moss rounded-sm"
              >
                <SafeIcon name="X" className="text-2xl" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {cart.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center space-y-4 opacity-70">
                  <SafeIcon name="ShoppingCart" className="text-5xl text-gray-400" />
                  <p className="font-serif text-xl text-espresso">Your cart is empty.</p>
                  <p className="text-sm text-gray-500 max-w-xs">Explore our origins and find your next favorite roast.</p>
                  <button 
                    onClick={onClose}
                    className="mt-4 text-moss font-bold uppercase tracking-widest text-[10px] hover:text-moss-light transition-colors"
                  >
                    Continue Shopping
                  </button>
                </div>
              ) : (
                <ul className="space-y-6">
                  {cart.map((item) => (
                    <li key={item.id} className="flex gap-4 bg-white p-4 shadow-sm border border-gray-100">
                      <div className="w-20 h-24 bg-gray-100 shrink-0">
                        {item.image ? (
                          <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-300"><SafeIcon name="Coffee" /></div>
                        )}
                      </div>
                      <div className="flex-1 flex flex-col justify-between">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-bold text-espresso text-sm leading-tight">{item.title || item.name}</h3>
                            <p className="text-[10px] uppercase tracking-wider text-gray-500 mt-1">{item.roast || item.frequency || item.type}</p>
                          </div>
                          <button 
                            onClick={() => removeFromCart(item.id)}
                            aria-label={`Remove ${item.name} from cart`}
                            className="text-gray-400 hover:text-red-500 transition-colors p-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-moss"
                          >
                            <SafeIcon name="Trash2" className="text-sm" />
                          </button>
                        </div>
                        <div className="flex justify-between items-center mt-4">
                          <div className="flex items-center border border-gray-200">
                            <button 
                              onClick={() => updateQuantity(item.id, -1)}
                              disabled={item.qty <= 1}
                              aria-label="Decrease quantity"
                              className="px-2 py-1 text-gray-500 hover:text-espresso disabled:opacity-30 focus:outline-none focus-visible:bg-gray-100"
                            >
                              <SafeIcon name="Minus" className="text-xs" />
                            </button>
                            <span className="px-3 py-1 text-sm font-medium text-espresso min-w-[2.5rem] text-center" aria-live="polite">
                              {item.qty}
                            </span>
                            <button 
                              onClick={() => updateQuantity(item.id, 1)}
                              aria-label="Increase quantity"
                              className="px-2 py-1 text-gray-500 hover:text-espresso focus:outline-none focus-visible:bg-gray-100"
                            >
                              <SafeIcon name="Plus" className="text-xs" />
                            </button>
                          </div>
                          <span className="font-serif font-bold text-espresso">${(item.price * item.qty).toFixed(2)}</span>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {cart.length > 0 && (
              <div className="p-6 bg-white border-t border-gray-200 shadow-[0_-4px_20px_rgba(0,0,0,0.05)]">
                <div className="flex justify-between items-center mb-6">
                  <span className="text-sm font-bold uppercase tracking-widest text-gray-500">Subtotal</span>
                  <span className="font-serif text-2xl font-bold text-espresso">${subtotal.toFixed(2)}</span>
                </div>
                <button className="w-full bg-moss hover:bg-moss-light text-cream py-4 font-bold uppercase tracking-[0.2em] text-[11px] transition-colors shadow-lg focus:outline-none focus-visible:ring-4 focus-visible:ring-moss/50">
                  Proceed to Checkout
                </button>
                <p className="text-center text-xs text-gray-400 mt-4 italic">Shipping and taxes calculated at checkout.</p>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;